/**
 * Renames a File object.
 *
 * **Extension Handling:**
 * - The function enforces the file's *original* extension to preserve integrity.
 * - If `newName` includes an extension (e.g., "newname.png"), that extension is replaced
 * by the original one.
 * - If `newName` has no extension, the original extension is appended.
 *
 * @param {File} file - The original File object.
 * @param {string} newName - The desired new name (with or without extension).
 * @returns {File} A new File object with the updated name.
 */
export function renameFile(file: File, newName: string): File {
	const originalExtension = extractExtension(file.name);
	const newNameExtension = extractExtension(newName);

	let finalName = newName;

	if (!newNameExtension) {
		finalName += originalExtension;
	}

	const blob = file.slice(0, file.size, file.type);
	const newFile = new File([blob], finalName, {
		type: file.type,
		lastModified: file.lastModified,
	});
	if (newFile.name !== finalName) {
		Object.defineProperty(newFile, "name", {
			value: finalName,
			writable: false,
		});
	}
	return newFile;
}

function extractExtension(str: string) {
	const lastDotIndex = str.lastIndexOf(".");
	const hasExtension = lastDotIndex > 0;
	const extension = hasExtension ? str.slice(lastDotIndex) : null;
	return extension;
}
